import styles from "./mortgage.module.css"
import React from "react"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import * as graphUtil from "shared/graphs/util"
import {DoubleBar} from "story/components/graphs/bar"
import assign from "object-assign"
import * as loan from "shared/loan-payoff"
import AccessibilityTable from "activities/components/shared/accessibility-table"

/* Data for graph format:
[
[year, value],
[year, value],
[year, value]
]
*/

const graphData = (balance, rate, payment, term) => {
  let bal = balance
  let principal = 0
  let interest = 0
  let totalInterest = 0
  const data = Array.from(Array((term + 1) * 12)).reduce((memo, x, idx) => {
    if (idx === 0) return memo
    else {
      const towardPrincipal = loan.getPrincipalPayment(bal, payment, rate) || payment
      bal -= towardPrincipal
      principal += towardPrincipal
      interest += payment - towardPrincipal
      if (idx > 0 && idx % 12 === 0) {
        memo.push([idx / 12, principal, interest])
        totalInterest += interest
        principal = 0
        interest = 0
      }
      return memo
    }
  }, [])

  return {data, totalInterest}
}

const noInterest = (p, t) => p * t * 12

const calcThings = ({monthlyPayment, downPayment, term, rate}) => {
  const loanAmount = loan.calculateLoanAmount(monthlyPayment, rate, term * 12) || noInterest(monthlyPayment, term)
  const homePrice = downPayment + loanAmount
  const maxMortgage = homePrice - downPayment
  const {data, totalInterest} = graphData(maxMortgage, rate, monthlyPayment, term)
  return {
    homePrice,
    maxMortgage,
    totalInterest,
    data
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const state = getState()
    const {monthlyPayment, downPayment, term, rate, resetKey} = state
    const {id, theme} = getProps()
    const descriptionId = "preferred-monthly-payment-desc"

    const decimalRate = rate / 100

    const {homePrice, maxMortgage, totalInterest, data} = calcThings({monthlyPayment, downPayment, term, rate: decimalRate})

    const accessibilityTableHeadings = [
      {
        name: "Year",
        key: "year"
      },
      {
        name: "Principal",
        key: "principal"
      },
      {
        name: "Interest",
        key: "interest"
      }
    ]

    const accessibilityTableData = data.map(([year, principal, interest]) => ({
      year,
      principal: util.formatDollarsNoCents(principal),
      interest: util.formatDollarsNoCents(interest)
    }))

    const accessibilityTableLabel = "Mortgage Estimation Calculator Results"

    return (
      <div>
        <shared.Content>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer>
              <div>
                <inputs.CalcInput
                  id={`${id}-input`}
                  ariaDescribedBy={descriptionId}
                  title={"Preferred Monthly Payment"}
                  onChange={updateState("monthlyPayment", util.fromString)}
                  initialValue={monthlyPayment}
                  resetKey={resetKey}
                />
                <inputs.Label className="gray-subtitle" id={descriptionId}>
                  *Principal & interest only
                </inputs.Label>
              </div>
              <inputs.SmallCalcInputWithTitle id={id} title="Down Payment" onChange={updateState("downPayment", util.fromString)} initialValue={downPayment} resetKey={resetKey} />
              <inputs.RangeInput
                id={id}
                title="Preferred Loan Term"
                onChange={updateState("term", util.fromString)}
                initialValue={term}
                min={5}
                max={30}
                step={5}
                valueType="years-full"
                units="years"
                resetKey={resetKey}
              />
              <inputs.RangeInput
                id={id}
                title="Interest Rate"
                onChange={updateState("rate", parseFloat)}
                initialValue={rate}
                min={0}
                max={30}
                step={0.05}
                units="%"
                valueType="percent"
                resetKey={resetKey}
              />
            </shared.InputsContainer>
            <shared.ChartContainer>
              <DoubleBar
                isStacked={true}
                staggerTicks={true}
                yAxis="Annual Payments"
                xAxis={"Years"}
                values={data}
                tooltip={[
                  "Year",
                  {name: "Principal", type: "currency", color: graphUtil.getColor(theme, 1)},
                  {name: "Interest", type: "currency", color: graphUtil.getColor(theme, 4)}
                ]}
                type="calculator"
                ariaLabel="This line graph visually represents how much you will pay toward the interest and principal of your mortgage, by year, based on your input. Read below to find the home price, the maximum mortgage you can afford, and the interest you would pay."
                formatter="currency"
                colors={[graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
              />
            </shared.ChartContainer>
            <shared.SummaryContainer className={styles["summary"]}>
              <callouts.Callout>
                <callouts.Detail
                  label="Home Price"
                  result={util.formatDollarsNoCents(util.ifNegative(homePrice))}
                  tooltipText="The highest home price we recommend, based on your preferred monthly payment, down payment, loan term, and interest rate."
                  dataTestId="home-price"
                />
                <callouts.Detail
                  label="Maximum Mortgage"
                  result={util.formatDollarsNoCents(util.ifNegative(maxMortgage))}
                  tooltipText="Your home price less your down payment, and the maximum mortgage you can afford based on your parameters."
                  dataTestId="max-mortgage"
                />
                <callouts.Detail
                  label="Interest Paid"
                  result={util.formatDollarsNoCents(totalInterest)}
                  tooltipText="The total amount of interest you’ll pay over the lifetime of your loan. This amount does not include principal payments."
                  dataTestId="total-interest"
                />
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {homePrice, maxMortgage, totalInterest} = calcThings(state)
      return assign(state, {
        homePrice,
        maxMortgage,
        totalInterest
      })
    },
    defaultProps: {
      id: "mortgage-estimation",
      className: "mortgage-estimation",
      title: "Mortgage Estimation Calculator",
      subtitle: "See what mortgage you can afford based on your preferred payments, loan term, and interest rate."
    },
    defaultState: {
      monthlyPayment: 1800,
      downPayment: 20000,
      term: 30,
      rate: 6.5
    }
  }
)
